import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  PreventionCatalogService,
  MarketCatalogFamily,
  MarketCatalogBilan,
} from '../../core/services/prevention-catalog.service';
import { PreventionGroupHeaderComponent } from '../../component/prevention-group-header/prevention-group-header.component';

interface BilanFamilyGroup {
  family: MarketCatalogFamily;
  bilans: MarketCatalogBilan[];
}

/**
 * Catalogue des bilans Waitora (Lot 9.0-E.E.C) -- lecture seule, aucune écriture, aucune donnée
 * médicale (needId/coverage/priorityTier jamais exposés par le backend, jamais réintroduits ici).
 * Regroupement par CatalogFamily (commercial), jamais par catégorie médicale. Recherche client-side
 * uniquement (8 familles / 22 bilans, aucune pagination/tri serveur justifié).
 */
@Component({
  selector: 'app-prevention-bilan-catalog',
  standalone: true,
  imports: [TranslatePipe, RouterLink, PreventionGroupHeaderComponent],
  templateUrl: './prevention-bilan-catalog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./prevention-bilan-catalog.component.scss'],
})
export class PreventionBilanCatalogComponent {
  private readonly catalogSvc = inject(PreventionCatalogService);
  private readonly router = inject(Router);

  readonly search = signal('');
  readonly loadError = signal(false);

  private readonly families = toSignal(
    this.catalogSvc.getFamilies().pipe(
      tap(() => this.loadError.set(false)),
      catchError(() => { this.loadError.set(true); return of(null); })
    ),
    { initialValue: null as MarketCatalogFamily[] | null }
  );

  private readonly bilans = toSignal(
    this.catalogSvc.getBilans().pipe(
      catchError(() => { this.loadError.set(true); return of(null); })
    ),
    { initialValue: null as MarketCatalogBilan[] | null }
  );

  readonly loading = computed(() => !this.loadError() && (this.families() === null || this.bilans() === null));

  /** Catalogue complet groupé par famille, TOUJOURS trié par family.position -- jamais l'ordre implicite du backend. */
  private readonly groupedByFamily = computed<BilanFamilyGroup[]>(() => {
    const families = this.families() ?? [];
    const bilans = this.bilans() ?? [];
    return families
      .slice()
      .sort((a, b) => a.position - b.position)
      .map(family => ({
        family,
        // Ordre des bilans PRESERVE tel que renvoyé par le backend (aucun champ position sur le
        // bilan lui-même dans ce contrat -- ne pas inventer un critère de tri arbitraire).
        bilans: bilans.filter(b => b.familyCode === family.code),
      }));
  });

  /**
   * Recherche insensible à la casse sur bilan.name / family.label / item.label (section 12 du lot)
   * -- pas de normalisation des accents (aucun helper existant dans le repo, infrastructure jugée
   * disproportionnée pour 22 lignes). Une famille sans bilan visible (recherche ou catalogue
   * réellement vide) n'est jamais affichée.
   */
  readonly filteredGroups = computed<BilanFamilyGroup[]>(() => {
    const query = this.search().trim().toLowerCase();
    return this.groupedByFamily()
      .map(group => ({
        family: group.family,
        bilans: !query ? group.bilans : group.bilans.filter(bilan => this.matches(bilan, group.family, query)),
      }))
      .filter(group => group.bilans.length > 0);
  });

  private matches(bilan: MarketCatalogBilan, family: MarketCatalogFamily, query: string): boolean {
    if (bilan.name.toLowerCase().includes(query)) return true;
    if (family.label.toLowerCase().includes(query)) return true;
    return bilan.items.some(item => item.label.toLowerCase().includes(query));
  }

  /** Vide au sens global (catalogue vraiment vide) -- distinct d'une recherche sans résultat. */
  readonly isGloballyEmpty = computed(() => !this.loading() && !this.loadError() && (this.bilans()?.length ?? 0) === 0);

  readonly hasSearchNoResults = computed(() =>
    !this.loading() && !this.loadError() && !this.isGloballyEmpty() &&
    this.search().trim().length > 0 && this.filteredGroups().length === 0
  );

  onSearchChanged(value: string): void {
    this.search.set(value);
  }

  /**
   * Destination déterministe (jamais window.history.back()) -- reflète le fil d'Ariane affiché
   * ("Prévention · Offres & déploiement · Catalogue des bilans") : ce bouton "Retour" a longtemps
   * renvoyé vers Site > Prévention (ancien point d'entrée avant que "Catalogue des bilans" ne soit
   * accessible depuis le domaine Groupe via Mes Offres) -- un simple `/site?tab=prevention` alors
   * que rien dans le fil d'Ariane ne mentionne Site amenait l'utilisateur sur un écran sans rapport
   * (audit QA). Aucun lien depuis Site ne pointe plus vers cette route.
   */
  goBack(): void {
    this.router.navigate(['/prevention-offres']);
  }
}
